import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import {
  Calendar,
  CalendarDays,
  Rocket,
  Timer,
  Clock,
  ChevronDown,
  Gift
} from "lucide-react";
import { getUpcomingLaunches } from '../../utils/api';
import ScrollToTop from '../../components/layout/ScrollToTop';

const UpcomingLaunches = () => {
  const [filter, setFilter] = useState('all');
  const [sortField, setSortField] = useState('launchDate');
  const [sortOrder, setSortOrder] = useState('asc');
  const [launches, setLaunches] = useState([]);
  const [loading, setLoading] = useState(true);
  let imageUrl = process.env.REACT_APP_IMAGE_URL;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset();
    const adjustedDate = new Date(date.getTime() + offset * 60 * 1000);
    return adjustedDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCountdown = (dateString) => {
    if (!dateString) return '';
    const launchDate = new Date(dateString);
    const now = new Date();
    const diffTime = launchDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Launched';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days`;
  };

  useEffect(() => {
    const fetchLaunches = async () => {
      try {
        const data = await getUpcomingLaunches();
        setLaunches(data.data);
      } catch (error) {
        console.error("Error fetching launches:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLaunches();
  }, []);

  const upcomingLaunches = [
    {
      id: 1,
      name: "CloudStack",
      category: "Cloud Computing",
      description: "Enterprise-grade cloud infrastructure management platform for scaling modern applications",
      launchDate: "May 15, 2025",
      logo: "C",
      logoColor: "bg-blue-500",
      featured: true,
      countdown: "7 days",
      hasDemo: true,
      hasExclusiveOffer: true,
    },
    {
      id: 2,
      name: "SalesBoost",
      category: "Sales Automation",
      description: "AI-powered sales automation tool to accelerate your pipeline and close more deals",
      launchDate: "May 17, 2025",
      logo: "S",
      logoColor: "bg-green-500",
      featured: false,
      countdown: "9 days",
      hasDemo: true,
      hasExclusiveOffer: false,
    },
    {
      id: 3,
      name: "DesignFlow",
      category: "Design Tools",
      description: "Collaborative design platform built for remote creative teams",
      launchDate: "May 22, 2025",
      logo: "D",
      logoColor: "bg-purple-500",
      featured: true,
      countdown: "14 days",
      hasDemo: false,
      hasExclusiveOffer: true,
    },
    {
      id: 4,
      name: "TaskMaster",
      category: "Productivity",
      description: "Task management system that organizes your workflow with powerful automation",
      launchDate: "May 25, 2025",
      logo: "T",
      logoColor: "bg-red-500",
      featured: false,
      countdown: "17 days",
      hasDemo: true,
      hasExclusiveOffer: false,
    },
    {
      id: 5,
      name: "FinTrack",
      category: "Finance",
      description: "Financial tracking and management platform for small businesses and startups",
      launchDate: "June 1, 2025",
      logo: "F",
      logoColor: "bg-yellow-500",
      featured: false,
      countdown: "24 days",
      hasDemo: true,
      hasExclusiveOffer: true,
    },
    {
      id: 6,
      name: "HealthSync",
      category: "Healthcare",
      description: "Patient management system for healthcare professionals with seamless EHR integration",
      launchDate: "June 5, 2025",
      logo: "H",
      logoColor: "bg-teal-500",
      featured: true,
      countdown: "28 days",
      hasDemo: true,
      hasExclusiveOffer: false,
    },
    {
      id: 7,
      name: "EcoMonitor",
      category: "Sustainability",
      description: "Environmental monitoring platform for businesses to track and reduce their carbon footprint",
      launchDate: "June 10, 2025",
      logo: "E",
      logoColor: "bg-emerald-500",
      featured: false,
      countdown: "33 days",
      hasDemo: false,
      hasExclusiveOffer: true,
    },
    {
      id: 8,
      name: "DevSync",
      category: "Development",
      description: "Collaborative code editor with real-time synchronization for developer teams",
      launchDate: "June 15, 2025",
      logo: "D",
      logoColor: "bg-indigo-500",
      featured: true,
      countdown: "38 days",
      hasDemo: true,
      hasExclusiveOffer: true,
    },
  ];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const filteredLaunches = filter === 'all'
    ? upcomingLaunches
    : filter === 'featured'
      ? launches.filter(launch => launch.featured)
      : filter === 'demo'
        ? launches.filter(launch => launch.hasDemo)
        : launches.filter(launch => launch.hasExclusiveOffer);

  const sortedLaunches = [...filteredLaunches].sort((a, b) => {
    if (sortField === 'name') {
      return sortOrder === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    } else if (sortField === 'category') {
      return sortOrder === 'asc'
        ? a.category.localeCompare(b.category)
        : b.category.localeCompare(a.category);
    } else if (sortField === 'launchDate') {
      // Simple string comparison for dates in this format
      return sortOrder === 'asc'
        ? a.launchDate.localeCompare(b.launchDate)
        : b.launchDate.localeCompare(a.launchDate);
    }
    return 0;
  });

  return (
    <div className="container mx-auto px-4 py-12 max-w-7xl">
      <ScrollToTop />
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Rocket className="h-8 w-8 text-purple-500" />
            Upcoming Launches
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl">
            Discover innovative products preparing to launch soon and get early access to exclusive offers.
          </p>
        </div>

      </div>

      {/* Table display */}
      <div className="overflow-x-auto bg-white rounded-lg border shadow">
        <table className="w-full caption-bottom text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th
                onClick={() => handleSort('name')}
                className="h-12 px-4 text-left align-middle font-medium text-gray-500 hover:bg-gray-100 cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <span>Product</span>
                  {sortField === 'name' && (
                    <ChevronDown className={`h-4 w-4 ml-1 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </div>
              </th>
              <th
                onClick={() => handleSort('category')}
                className="h-12 px-4 text-left align-middle font-medium text-gray-500 hover:bg-gray-100 cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <span>Category</span>
                  {sortField === 'category' && (
                    <ChevronDown className={`h-4 w-4 ml-1 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </div>
              </th>
              <th className="h-12 px-4 text-left align-middle font-medium text-gray-500">
                Description
              </th>
              <th
                onClick={() => handleSort('launchDate')}
                className="h-12 px-4 text-left align-middle font-medium text-gray-500 hover:bg-gray-100 cursor-pointer whitespace-nowrap"
              >
                <div className="flex items-center justify-between">
                  <span>Launch Date</span>
                  {sortField === 'launchDate' && (
                    <ChevronDown className={`h-4 w-4 ml-1 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </div>
              </th>
              <th className="h-12 px-4 text-left align-middle font-medium text-gray-500 whitespace-nowrap">
                Status
              </th>



            </tr>
          </thead>
          <tbody>
            {launches.map((launch) => (
              <tr
                key={launch.id}
                className="border-b hover:bg-gray-50 transition-colors"
              >
                <td className="p-4 align-middle">
                  <div className="flex items-center space-x-3">
                    <div className="relative h-12 w-12 rounded-full border-2 border-purple-100 overflow-hidden">
                      {launch.logo ? (
                        <img src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + launch.logo} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-medium">
                          {launch.name.substring(0, 2)}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-semibold">{launch.name}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {launch.featured && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-semibold rounded-full">
                            Featured
                          </span>
                        )}
                        {launch.hasDemo && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                            Demo
                          </span>
                        )}
                        {launch.hasExclusiveOffer && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                            Exclusive
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-4 align-middle text-gray-700">
                  <div>
                    {launch.industry_name}
                  </div>

                  {launch.offer_name && (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
                      <Gift className="w-3 h-3 mr-1" />
                      {launch.offer_name}
                    </span>
                  )}
                </td>
                <td className="p-4 align-middle text-gray-600 line-clamp-2 max-w-xs">
                  {launch.tagline}
                </td>
                <td className="p-4 align-middle whitespace-nowrap">
                  <div className="flex items-center text-gray-600">
                    <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                    <span>{formatDate(launch.launch_date_start)}</span>
                  </div>
                </td>
                <td className="p-4 align-middle">
                  <div className="flex items-center text-purple-600 font-medium">
                    <Timer className="h-4 w-4 mr-2" />
                    <span>{getCountdown(launch.launch_date_start)}</span>
                  </div>
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>


    </div>
  );
};

export default UpcomingLaunches;